import { decodeBase64 } from '../../../../language/decodeBase64';
import { BROWSER, getBrowser } from '../../../../language/browser';
import { EMPTY_MEDIA, Media } from '../media';
import { IAppState } from '../../../../core/redux/IAppState';
import * as R from 'ramda';
import { PassPdf } from './passPdf';
import { GuestDetails } from '../../../guestDetails';
import { assoc } from '../../../../language/module';
import { PassPrintConfig } from '../../../../auth/config/passPrintConfig';


/**
 * callback function for opening a new window with the PDF as the data.
 * takes the window object as a parameter.
 */
export const onNewChromiumTabOpened = (
  winPrintPDF: Window,
  callback: (success: boolean) => void,
): void => {
  winPrintPDF.document.close();
  winPrintPDF.focus();
  winPrintPDF.print();
  callback(true);
};


/**
 * Prints the given file data in browsers other than Chrome, IE11, and Edge.
 */
export const chromiumPrint = (
  win: Window,
  dataBlob: Blob | undefined,
  callback: (success: boolean) => void,
): void => {
  if (!win || !dataBlob) {
    callback(false);
    return;
  }

  const data        = (win as any).URL.createObjectURL(dataBlob);
  const winPrintPDF = win.open(data, '_blank');

  if (!winPrintPDF) {
    callback(false);
    return;
  }

  const callCallback = (
    () => {
      let wasCalled = false;

      return (status) => {
        if (wasCalled) {
          return;
        }

        wasCalled = true;
        callback(status);
      };
    }
  )();

  winPrintPDF.onload  = () => onNewChromiumTabOpened(winPrintPDF, callCallback);
  winPrintPDF.onerror = () => callCallback(false);

  // If we haven't heard back from the tab in 3 seconds, assume success
  window.setTimeout(() => callCallback(true), 3000);
};


/**
 * Prints the given file data in IE11 or Edge.
 */
export const iePrint = (
  win: Window,
  data: Blob | undefined,
  callback: (success: boolean) => void,
): void => {
  if (!data || !win.navigator) {
    callback(false);
    return;
  }

  (win.navigator as any).msSaveOrOpenBlob(data);
  callback(true);
};


/**
 * Prints the given file data in browsers other than Chrome, IE11, and Edge.
 */
export const iFramePrint = (
  win: Window,
  data: Blob | undefined,
  callback: (success: boolean) => void,
): void => {
  if (!win || !data) {
    callback(false);
    return;
  }
  const url            = (win as any).URL.createObjectURL(data);
  const printContainer = win.document.createElement('iframe');

  printContainer.width  = '1px';
  printContainer.height = '1px';

  win.document.body.appendChild(printContainer);

  printContainer.onerror = () => callback(false);
  printContainer.onload  = () => {
    printContainer.focus();

    if (printContainer.contentWindow && printContainer.contentWindow.print) {
      printContainer.contentWindow.print();
    }

    setTimeout(
      () => {
        (win as any).URL.revokeObjectURL(url); // We've handed the PDF to the iframe, we don't need its URL anymore
      },
      1000,
    );

    callback(true);
  };

  printContainer.src = url;
};


/**
 * Takes a PDF string and attempts to open it in a new tab with the print dialog showing.
 * @param win - The Window object
 * @param passPdf - The PDF Base 64 encoded
 * @param callback - The callback to call after the new tab opens
 */
export const printPdf = (win: Window, passPdf: string, callback: (success: boolean) => void) => {
  const pdfBlob = decodeBase64(win, passPdf);
  const browser = getBrowser();

  switch (browser) {
    case BROWSER.CHROME:
    case BROWSER.FIRE_FOX:
    case BROWSER.OPERA:
      chromiumPrint(win, pdfBlob, callback);
      return;

    case BROWSER.IE11:
      iePrint(win, pdfBlob, callback);
      return;

    default:
      iFramePrint(win, pdfBlob, callback);
  }
};


/**
 * Returns a pdf for printing for a guest if one exists, otherwise returns undefined.
 */
export const pdfForGuest = (
  state: IAppState,
  guestId: string,
): PassPdf | undefined => R.find(passPrintPdf => passPrintPdf.guestIds.includes(guestId), state.passPrinting.pdfs);


/**
 * Takes the format header off  the given PDF string. If your PDF string isn't properly formatted, or if you don't have
 * a PDF string at all, then you'll likely get back an empty string (depending on the contents of your input string; you
 * may get back something pretty garbled if there's something there to try and parse).
 */
export const removePdfHeader = (pdf: string | undefined): string => R.last((pdf || '').split(',')) || '';


/**
 * Removes the guest's old media (if applicable) and adds the new media generated from printing the guest's Ikon pass
 * again. The media added to the guest is a placeholder that stubs out the actual media that the server should have
 * added.
 */
export const updateGuestMedia = (
  guest: GuestDetails,
  newMediaId: string,
  printDate: Date | undefined,
): GuestDetails => {
  // A guest media represents the physical card printed as their pass -- when we print we give the guest a new media
  const newMedia = ({...EMPTY_MEDIA,  chipID: undefined, mediaID: newMediaId, printDate: printDate }) as unknown as Media;

  // Make new state by adding the new media to the beginning of the media array as this is the newest media.  This method is currently 
  // only called from the reissueMediaAction reducer which no longer has the guest's old mediaId (now uses entitlement type).
  return assoc(
    'media',
    [newMedia].concat((guest.media || [])),
    guest,
  );
};

/**
 * Pass reprint charge notification
 */
export const NO_REPRINT_FEE = 0;  /* Current fee structure is a flat $5 per print  */
export const DEFAULT_REPRINT_FEE = 5;   

export const calculatePassReprintFee = (printCount: number, reprintFee: number | undefined) : number => {
  return reprintFee ?? DEFAULT_REPRINT_FEE;   
};               

export const getPrintCountStartDate = (passPrintConfig: PassPrintConfig): Date => {
  if (passPrintConfig === undefined || passPrintConfig.printCountStartDate === undefined) {      
    const month = new Date().getMonth();
    const year = month >= 9
        ? new Date().getFullYear()
        : new Date().getFullYear() - 1;
    return new Date(year, 9, 1);    // Default to Oct 1 of current or previous year
  }
  return new Date(passPrintConfig.printCountStartDate);
}
